// Wishlist heart-button behavior, shared by every card-product.liquid render
// and the product-page button in sections/main-product.liquid. Wishlist data
// only ever exists server-side for logged-in customers (App Proxy ->
// app/routes/proxy.wishlist.tsx -> WishlistItem table) - there is
// deliberately no guest/localStorage fallback: a logged-out click opens a
// "log in to save this" modal instead of silently toggling any local state.
(() => {
  if (window.__wrpgWishlistLoaded) return;
  window.__wrpgWishlistLoaded = true;

  const ENDPOINT = "/apps/wrpg/wishlist";
  const LOGIN_MODAL_ID = "WrpgWishlistLoginModal";

  const state = {
    mode: "unknown",
    // handle -> Set of variant GIDs (empty string = null/legacy/single variant)
    items: new Map(),
    ready: null,
  };

  function handlesFromItems(items) {
    return [...items.keys()];
  }

  function variantSetFor(handle) {
    return state.items.get(handle) || new Set();
  }

  function isActive(handle) {
    return variantSetFor(handle).size > 0;
  }

  function syncButtons() {
    document.querySelectorAll("[data-wrpg-wishlist-toggle]").forEach((button) => {
      const handle = button.dataset.productHandle;
      const active = isActive(handle);
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) {
        button.setAttribute("aria-label", label);
        const visibleLabel = button.querySelector(".wrpg-wishlist-button__label");
        if (visibleLabel) visibleLabel.textContent = label;
      }
    });
  }

  function announce() {
    document.dispatchEvent(
      new CustomEvent("wrpg:wishlist:change", {
        detail: {
          handles: handlesFromItems(state.items),
          items: [...state.items.entries()].map(([handle, variants]) => ({
            handle,
            variantIds: [...variants],
          })),
        },
      }),
    );
  }

  function applyServerPayload(data) {
    const next = new Map();
    (data.items || []).forEach((item) => {
      const set = next.get(item.handle) || new Set();
      set.add(item.variantId || "");
      next.set(item.handle, set);
    });
    if (!data.items && Array.isArray(data.handles)) {
      data.handles.forEach((handle) => next.set(handle, new Set([""])));
    }
    state.items = next;
  }

  function openLoginModal(opener, reason) {
    const modal = document.getElementById(LOGIN_MODAL_ID);
    if (!modal || typeof modal.show !== "function") return;

    const headingEl = modal.querySelector("#WrpgWishlistModalHeading");
    const messageEl = modal.querySelector("#WrpgWishlistModalMessage");
    const ctaEl = modal.querySelector("#WrpgWishlistModalCta");
    const isError = reason === "error";

    if (headingEl) headingEl.textContent = isError ? modal.dataset.errorHeading : modal.dataset.loginHeading;
    if (messageEl) messageEl.textContent = isError ? modal.dataset.errorMessage : modal.dataset.loginMessage;
    if (ctaEl) ctaEl.hidden = isError;

    modal.show(opener);
  }

  function refreshExclusions() {
    if (!window.wrpgExclusions || !window.wrpgExclusions.applyServerPayload) return;
    fetch("/apps/wrpg/exclusions")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data) window.wrpgExclusions.applyServerPayload(data);
      })
      .catch(() => {});
  }

  function post(body) {
    return fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data) applyServerPayload(data);
        syncButtons();
        announce();
        refreshExclusions();
      })
      .catch(() => {});
  }

  function hasOnlyDefaultVariant(button) {
    return button.dataset.hasOnlyDefaultVariant === "true";
  }

  function labelsFrom(button) {
    return {
      headingWishlist: button.dataset.pickerHeading,
      hintWishlist: button.dataset.pickerHint,
      save: button.dataset.pickerSave || "Save",
      excludeAll: button.dataset.pickerExcludeAll,
      excludeAllActive: button.dataset.pickerExcludeAllActive,
      modeWishlist: button.dataset.pickerModeWishlist,
      modeExclude: button.dataset.pickerModeExclude,
      loading: button.dataset.pickerLoading || "Loading…",
      error: button.dataset.pickerError || "Couldn't load variants.",
      owned: button.dataset.pickerOwned || "Owned",
    };
  }

  function openPicker(button) {
    const handle = button.dataset.productHandle;
    const productId = button.dataset.productId;
    const launch = () => {
      const owned = (window.wrpgOwnership && window.wrpgOwnership.getOwnedVariantIds(productId)) || [];
      const wishlistIds = [...variantSetFor(handle)].map((id) => (id === "" ? null : id));
      const excludeIds =
        window.wrpgExclusions && window.wrpgExclusions.getVariantIds
          ? window.wrpgExclusions.getVariantIds(handle)
          : [];

      if (!window.wrpgVariantPickerModal) {
        toggleSimple(handle, productId);
        return;
      }

      window.wrpgVariantPickerModal.open({
        paintMode: "wishlist",
        handle,
        productId,
        wishlistVariantIds: wishlistIds,
        excludeVariantIds: excludeIds,
        ownedVariantIds: owned,
        opener: button,
        labels: labelsFrom(button),
      });
    };

    const other = window.wrpgExclusions && window.wrpgExclusions.ready ? window.wrpgExclusions.ready() : Promise.resolve();
    Promise.resolve(other).then(launch).catch(launch);
  }

  function toggleSimple(handle, productId) {
    const willAdd = !isActive(handle);
    if (willAdd) {
      state.items.set(handle, new Set([""]));
    } else {
      state.items.delete(handle);
    }
    syncButtons();
    announce();
    post({ action: willAdd ? "add" : "remove", handle, productId });
  }

  function init() {
    state.ready = fetch(ENDPOINT)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error("wishlist request failed"))))
      .then((data) => {
        if (data && data.loggedIn) {
          state.mode = "server";
          applyServerPayload(data);
        } else {
          state.mode = "guest";
          state.items = new Map();
        }
      })
      .catch(() => {
        state.mode = "error";
        state.items = new Map();
      })
      .then(() => {
        syncButtons();
        announce();
      });
  }

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-wrpg-wishlist-toggle]");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    (state.ready || Promise.resolve()).then(() => {
      if (state.mode === "server") {
        const handle = button.dataset.productHandle;
        if (!handle) return;
        if (hasOnlyDefaultVariant(button)) {
          toggleSimple(handle, button.dataset.productId);
        } else {
          openPicker(button);
        }
        return;
      }
      openLoginModal(button, state.mode === "error" ? "error" : "guest");
    });
  });

  init();

  window.wrpgWishlist = {
    ready: () => state.ready || Promise.resolve(),
    isLoggedIn: () => state.mode === "server",
    getMode: () => state.mode,
    getHandles: () => handlesFromItems(state.items),
    getVariantIds: (handle) => [...variantSetFor(handle)].map((id) => (id === "" ? null : id)),
    applyServerPayload: (data) => {
      applyServerPayload(data);
      syncButtons();
      announce();
    },
    remove: (handle) =>
      (state.ready || Promise.resolve()).then(() => {
        if (state.mode === "server") toggleSimple(handle);
      }),
    sync: () => syncButtons(),
  };
})();
